import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import QRCode from 'react-qr-code';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { toast } from 'sonner';

import { useData } from '../../hooks/useData';
import { IUser } from '../../constants/types';

interface Branch {
  id: number;
  branch_name: string;
  owner_userid: string;
}

interface QRCustomText {
  title: string;
  content: string;
}

function QRCodeGenerator() {
  const [branchId, setBranchId] = useState<string>('');
  const [qrCodeValue, setQrCodeValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [ownerInfo, setOwnerInfo] = useState<IUser | null>(null);
  
  // 커스텀 텍스트 관련 상태
  const [customText, setCustomText] = useState<QRCustomText>({ title: '', content: '' });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [tempText, setTempText] = useState<QRCustomText>({ title: '', content: '' });
  
  const { user } = useData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // 로그인한 사용자의 지점 정보로 QR 코드 생성
    fetchOwnerInfo();
  }, [user]);

  /**
   * localStorage에서 커스텀 텍스트 정보를 불러오는 함수
   */
  const loadCustomText = (branchCode: string): QRCustomText => {
    try {
      const savedText = localStorage.getItem(`qr_custom_text_${branchCode}`);
      if (savedText) {
        return JSON.parse(savedText);
      }
    } catch (error) {
      console.error('커스텀 텍스트 로드 오류:', error);
    }
    return { title: '', content: '' };
  };

  /**
   * localStorage에 커스텀 텍스트 정보를 저장하는 함수
   */
  const saveCustomText = (branchCode: string, text: QRCustomText): void => {
    try {
      localStorage.setItem(`qr_custom_text_${branchCode}`, JSON.stringify(text));
    } catch (error) {
      console.error('커스텀 텍스트 저장 오류:', error);
      toast.error('텍스트 저장에 실패했습니다.');
    }
  };



  const fetchOwnerInfo = () => {
    try {
      setLoading(true);
      
      if (!user || !user.branch_id) {
        toast.error('로그인 정보를 찾을 수 없습니다.');
        return;
      }



      // 로그인 시 받아온 branch_id 사용
      const branchCode = user.branch_id.toString();
      
      console.log('🏪 지점 정보 확인:', {
        userId: user.id,
        branchId: user.branch_id,
        branchCode,
        branchName: user.branchInfo?.branch_name,
        userType: user.userType,
      });

      setBranchId(branchCode);
      setOwnerInfo(user as IUser);

      // 저장된 커스텀 텍스트 불러오기
      const savedCustomText = loadCustomText(branchCode);
      setCustomText(savedCustomText);

      // 기존에 생성된 QR 코드가 있는지 확인
      const savedQRCode = localStorage.getItem(`qr_code_${branchCode}`);
      if (savedQRCode) {
        setQrCodeValue(savedQRCode);
      } else {
        generateQRCode(branchCode);
      }
    } catch (error) {
      console.error('Owner 정보 가져오기 오류:', error);
      toast.error('Owner 정보를 가져오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = (branchCode: string) => {
    setLoading(true);
    try {
      // QR 코드 URL 생성 (Branch ID 기준)
      const reviewUrl = `${window.location.origin}/review/${branchCode}`;
      setQrCodeValue(reviewUrl);

      // localStorage에 QR 코드 저장 (영구 보관)
      localStorage.setItem(`qr_code_${branchCode}`, reviewUrl);

      toast.success('QR 코드가 생성되었습니다!');
    } catch (error) {
      console.error('QR 코드 생성 오류:', error);
      toast.error('QR 코드 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 순수 QR 코드만 다운로드하는 함수
   */
  const downloadQRCodeOnly = () => {
    const svg = document.getElementById('qrcode-svg');
    if (!svg) {
      toast.error('QR 코드를 찾을 수 없습니다.');
      return;
    }

    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        toast.error('Canvas를 생성할 수 없습니다.');
        return;
      }

      // 캔버스 크기 설정 (QR 코드만)
      const qrSize = 400;
      const padding = 40;
      const totalSize = qrSize + (padding * 2);
      
      canvas.width = totalSize;
      canvas.height = totalSize;

      // 배경 그리기 (흰색)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, totalSize, totalSize);

      // QR 코드 이미지 생성 및 그리기
      const qrImg = new Image();
      qrImg.onload = () => {
        // QR 코드만 그리기
        ctx.drawImage(qrImg, padding, padding, qrSize, qrSize);

        // 다운로드 실행
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `QR_Code_${branchId}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();

        toast.success('QR 코드가 다운로드되었습니다!');
      };

      qrImg.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    } catch (error) {
      console.error('QR 코드 다운로드 오류:', error);
      toast.error('QR 코드 다운로드에 실패했습니다.');
    }
  };

  /**
   * 커스텀 텍스트가 포함된 QR 코드를 다운로드하는 함수
   */
  const downloadQRCodeWithText = () => {
    const svg = document.getElementById('qrcode-svg');
    if (!svg) {
      toast.error('QR 코드를 찾을 수 없습니다.');
      return;
    }

    try {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        toast.error('Canvas를 생성할 수 없습니다.');
        return;
      }

      // 캔버스 크기 설정 (텍스트 공간 포함)
      const qrSize = 400;
      const padding = 40;
      const qrTextMargin = 30; // QR 코드와 텍스트 사이 마진
      const textAreaHeight = customText.title || customText.content ? 140 : 0; // 텍스트 영역 높이 증가
      const totalWidth = qrSize + (padding * 2);
      const totalHeight = qrSize + (padding * 2) + (textAreaHeight ? textAreaHeight + qrTextMargin : 0);
      
      canvas.width = totalWidth;
      canvas.height = totalHeight;

      // 배경 그리기 (흰색)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, totalWidth, totalHeight);

      // QR 코드 이미지 생성 및 그리기
      const qrImg = new Image();
      qrImg.onload = () => {
        // QR 코드 그리기
        ctx.drawImage(qrImg, padding, padding, qrSize, qrSize);

        // 커스텀 텍스트 추가
        if (customText.title || customText.content) {
          const textStartY = qrSize + padding + qrTextMargin + 20;
          
          // 제목 그리기
          if (customText.title) {
            ctx.fillStyle = '#000000';
            ctx.font = 'bold 24px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(customText.title, totalWidth / 2, textStartY);
          }
          
          // 본문 그리기
          if (customText.content) {
            ctx.fillStyle = '#333333';
            ctx.font = '18px Arial, sans-serif';
            ctx.textAlign = 'center';
            
            // 긴 텍스트 처리 (줄바꿈)
            const maxWidth = totalWidth - (padding * 2);
            const words = customText.content.split(' ');
            let line = '';
            let y = textStartY + (customText.title ? 40 : 0); // 제목과 본문 사이 간격 증가
            
            for (let n = 0; n < words.length; n++) {
              const testLine = line + words[n] + ' ';
              const metrics = ctx.measureText(testLine);
              const testWidth = metrics.width;
              
              if (testWidth > maxWidth && n > 0) {
                ctx.fillText(line, totalWidth / 2, y);
                line = words[n] + ' ';
                y += 25;
              } else {
                line = testLine;
              }
            }
            ctx.fillText(line, totalWidth / 2, y);
          }
        }

        // 다운로드 실행
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `QR_Code_${branchId}_${customText.title || 'Custom'}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();

        toast.success('커스텀 QR 코드가 다운로드되었습니다!');
      };

      qrImg.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    } catch (error) {
      console.error('QR 코드 다운로드 오류:', error);
      toast.error('QR 코드 다운로드에 실패했습니다.');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(qrCodeValue)
      .then(() => {
        toast.success('QR 코드 URL이 복사되었습니다!');
      })
      .catch(() => {
        toast.error('복사에 실패했습니다.');
      });
  };

  /**
   * 텍스트 편집 다이얼로그를 여는 함수
   */
  const handleOpenEditDialog = () => {
    setTempText({ ...customText });
    setIsEditDialogOpen(true);
  };

  /**
   * 텍스트 편집을 저장하는 함수
   */
  const handleSaveCustomText = () => {
    setCustomText({ ...tempText });
    saveCustomText(branchId, tempText);
    setIsEditDialogOpen(false);
    toast.success('커스텀 텍스트가 저장되었습니다!');
  };

  /**
   * 텍스트 편집을 취소하는 함수
   */
  const handleCancelEdit = () => {
    setTempText({ ...customText });
    setIsEditDialogOpen(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} px={isMobile ? 2 : 3}>
        <Grid container spacing={3} justifyContent='center'>
          <Grid item xs={12} md={8} lg={6}>
            <Card>
              <CardContent>
                {loading ? (
                  <Box textAlign='center' py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* QR 코드 미리보기 */}
                    {qrCodeValue && (
                      <Box textAlign='center' mb={3}>
                        <Box
                          sx={{
                            width: isMobile ? '100%' : '300px',
                            maxWidth: '100%',
                            margin: '0 auto',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              p: isMobile ? 1.5 : 2,
                              backgroundColor: '#ffffff',
                              border: '1px solid #cccccc',
                              borderRadius: '4px',
                            }}
                          >
                            <QRCode
                              id='qrcode-svg'
                              value={qrCodeValue}
                              size={isMobile ? 200 : 250}
                              style={{
                                height: 'auto',
                                maxWidth: '100%',
                                width: '100%',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* 커스텀 텍스트 미리보기 */}
                    {(customText.title || customText.content) && (
                      <Box textAlign='center' mb={3}>
                        <Card variant='outlined' sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                          {customText.title && (
                            <Typography
                              variant='h6'
                              sx={{
                                fontWeight: 'bold',
                                mb: customText.content ? 1 : 0,
                                fontSize: isMobile ? '1rem' : '1.25rem',
                              }}
                            >
                              {customText.title}
                            </Typography>
                          )}
                          {customText.content && (
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{
                                fontSize: isMobile ? '0.875rem' : '1rem',
                                lineHeight: 1.5,
                              }}
                            >
                              {customText.content}
                            </Typography>
                          )}
                        </Card>
                      </Box>
                    )}

                    <Box textAlign='center' mb={2}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ 
                          mb: 2, 
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          wordBreak: 'break-all',
                          px: isMobile ? 1 : 0
                        }}
                      >
                        URL: {qrCodeValue}
                      </Typography>
                    </Box>

                    <Box textAlign='center'>
                      <Grid 
                        container 
                        spacing={isMobile ? 1 : 2} 
                        justifyContent='center'
                        direction={isMobile ? 'column' : 'row'}
                        sx={{
                          '& .MuiGrid-item': {
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '100%' : 'none'
                          }
                        }}
                      >
                        <Grid item xs={isMobile ? 12 : 'auto'}>
                          <Button
                            variant='contained'
                            color='secondary'
                            onClick={handleOpenEditDialog}
                            fullWidth={isMobile}
                            size={isMobile ? 'large' : 'medium'}
                            style={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              marginBottom: isMobile ? '8px' : '0',
                            }}
                          >
                            텍스트 편집
                          </Button>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 'auto'}>
                          <Button
                            variant='contained'
                            color='primary'
                            onClick={downloadQRCodeOnly}
                            disabled={!qrCodeValue}
                            fullWidth={isMobile}
                            size={isMobile ? 'large' : 'medium'}
                            style={{
                              backgroundColor: '#4caf50',
                              color: 'white',
                              marginBottom: isMobile ? '8px' : '0',
                            }}
                          >
                            QR 코드만 다운로드
                          </Button>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 'auto'}>
                          <Button
                            variant='contained'
                            color='primary'
                            onClick={downloadQRCodeWithText}
                            disabled={!qrCodeValue}
                            fullWidth={isMobile}
                            size={isMobile ? 'large' : 'medium'}
                            style={{
                              backgroundColor: '#2196f3',
                              color: 'white',
                              marginBottom: isMobile ? '8px' : '0',
                            }}
                          >
                            텍스트 포함 다운로드
                          </Button>
                        </Grid>
                        <Grid item xs={isMobile ? 12 : 'auto'}>
                          <Button
                            variant='outlined'
                            color='primary'
                            onClick={copyToClipboard}
                            disabled={!qrCodeValue}
                            fullWidth={isMobile}
                            size={isMobile ? 'large' : 'medium'}
                            style={{
                              backgroundColor: 'white',
                              color: '#2196f3',
                            }}
                          >
                            URL 복사
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 텍스트 편집 다이얼로그 */}
        <Dialog
          open={isEditDialogOpen}
          onClose={handleCancelEdit}
          maxWidth='sm'
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Typography variant='h6' component='div'>
              커스텀 텍스트 편집
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                label='제목'
                fullWidth
                variant='outlined'
                value={tempText.title}
                onChange={(e) => setTempText({ ...tempText, title: e.target.value })}
                placeholder='QR 코드에 표시할 제목을 입력하세요'
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 50 }}
                helperText={`${tempText.title.length}/50`}
              />
              
              <TextField
                label='본문'
                fullWidth
                multiline
                rows={4}
                variant='outlined'
                value={tempText.content}
                onChange={(e) => setTempText({ ...tempText, content: e.target.value })}
                placeholder='QR 코드에 표시할 본문을 입력하세요'
                inputProps={{ maxLength: 200 }}
                helperText={`${tempText.content.length}/200`}
              />
              
              <Divider sx={{ my: 2 }} />
              
              {/* 미리보기 */}
              {(tempText.title || tempText.content) && (
                <Box>
                  <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
                    미리보기:
                  </Typography>
                  <Card variant='outlined' sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                    {tempText.title && (
                      <Typography
                        variant='h6'
                        sx={{
                          fontWeight: 'bold',
                          mb: tempText.content ? 1 : 0,
                          fontSize: '1.1rem',
                        }}
                      >
                        {tempText.title}
                      </Typography>
                    )}
                    {tempText.content && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{
                          fontSize: '0.9rem',
                          lineHeight: 1.5,
                        }}
                      >
                        {tempText.content}
                      </Typography>
                    )}
                  </Card>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleCancelEdit}
              variant='outlined'
              color='primary'
              size='large'
              fullWidth={isMobile}
            >
              취소
            </Button>
            <Button
              onClick={handleSaveCustomText}
              variant='contained'
              color='primary'
              size='large'
              fullWidth={isMobile}
              sx={{ ml: isMobile ? 0 : 1, mt: isMobile ? 1 : 0 }}
            >
              저장
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default QRCodeGenerator;
